import type * as pf from '@amirada/lib-shared'
import { NodeType, PfCmdType } from '@amirada/lib-shared'
import type * as tp from '@base/utils/types'
import { def_footNote, ECarts, ElocalStorageType, type EScreenSize, ESpecialPage } from '@base/utils/constants'
import { fn_copyParam, fn_getNewRouterParams, fn_getNodesByPath } from '@base/utils/functions'
import { customerBuildCustom } from '@customerQuery'
import { useAppIndexStore } from '@base/stores/indexStore'
import { useInitStore } from '@base/stores/initStore'

// What a store request asks for: the navigation's paramData with the parts that deviate replaced.
// The path-dependent stores build their cache key from it, so this is also what decides which entry
// is read and written — see the composables, which use it for reading and fetching alike.
export interface IParamDataLocal {
  objPath?: string
  psx?: string
  sort?: string
  psn?: number[]
}

// Only the keys actually given are touched. Passing `psn: undefined` clears it, omitting `psn` keeps
// whatever the navigation has — and that distinction carries weight: a local filter has to replace
// the URL's, while a mask must not lose the page's sort just because it has none of its own. A plain
// `!== undefined` check could not tell the two apart.
export function ut_buildParamData(paramData: tp.IParamData, local: IParamDataLocal = {}): tp.IParamData {
  const res: tp.IParamData = { ...paramData }
  if (local.objPath) { res.namePath = local.objPath }
  const query: tp.IParamQueryUrlServer = { ...res.queryUrlServer }
  if ('psx' in local) { query.psx = local.psx || '' }
  if ('sort' in local) { query.sort = local.sort || '' }
  if ('psn' in local) { query.psn = local.psn }
  res.queryUrlServer = query
  return res
}

// Copy params only if set
export function ut_copyParams(sourceAr: any, destAr: any, defaultAr: any, setEmpty: boolean = false, noEmpty: boolean = false) {
  for (const key in defaultAr) {
    fn_copyParam(sourceAr, destAr, defaultAr, key, setEmpty, noEmpty)
  }
}

// Create a string combination of cache relevant parameters
//
// ⚑ The customer `custom` params belong in here IN FULL, not flag by flag. They change what the
// server answers, so two requests that differ only in `custom` must not share a store entry — and
// deciding per flag whether it "can collide" is a judgement that goes stale the moment a new flag
// appears. Serialising the whole object is right by construction. Key order is stable because a
// single function builds the object (customerBuildCustom).
export function ut_createParamDataString(paramData: tp.IParamData): string {
  const custom: pf.TCustom | undefined = customerBuildCustom(paramData)
  return [(paramData.rcco || ''),
    (paramData.lcda || ''),
    (paramData.queryUrlServer.rcre1 || ''),
    (paramData.queryUrlServer.rcre2 || ''),
    (paramData.queryUrlServer.rcst || ''),
    (paramData.queryUrlServer.stage || ''),
    (paramData.queryUrlServer.date || ''),
    (paramData.queryUrlServer.dops || ''),
    (custom ? JSON.stringify(custom) : '')].join('')
  /* (paramData.userData.userName || '')].join('') */
}

// Check name is a special page
export function ut_isSpecialPage(name: string): boolean {
  return !(name !== ESpecialPage.sp_search_result
    && name !== ESpecialPage.sp_custom) || ut_isCartPage(name)
}

// Check name is a cart page
export function ut_isCartPage(name: string): boolean {
  return !(name !== ECarts.ct_shopping_cart
    && name !== ECarts.ct_product_compare
    && name !== ECarts.ct_download_list
    && name !== ECarts.ct_favourites
    && name !== ECarts.ct_catalogue_create
    && name !== ECarts.ct_price_list_create
  )
}

export function ut_getCartType(specialPage: ESpecialPage): Partial<ECarts> | undefined {
  switch (specialPage) {
    case ESpecialPage.sp_favourites:
      return ECarts.ct_favourites
    case ESpecialPage.sp_product_compare:
      return ECarts.ct_product_compare
    case ESpecialPage.sp_catalogue_create:
      return ECarts.ct_catalogue_create
    case ESpecialPage.sp_price_list_create:
      return ECarts.ct_price_list_create
    case ESpecialPage.sp_download_list:
      return ECarts.ct_download_list
  }
}

// Join two paths, take care of leading and trailing slashes
export function ut_joinPathParts(parts: string[]) {
  for (let i = 0; i < parts.length; i++) {
    if (parts[i]) {
      parts[i] = (parts[i] || '').replaceAll('/', '')
    }
    else {
      parts.splice(i, 1)
    }
  }
  return parts.join('/')
}

// Return the URL path to a special page
export function ut_specialPagePath(page: ESpecialPage | ECarts): tp.IParamRouter {
  const path: string = useAppIndexStore().getRootPath + '/' + page
  return ut_returnNewRoute(path, {}, '')
}

// Convert a route with params to simple URL
export function ut_convertRouterParamsToURL(param: tp.IParamRouter) {
  let url: string = param.path || ''
  url = ut_addUrlParams(url, param.query || {})
  url += param.hash ? param.hash : ''
  return url
}

// Add a path to existing route object
export function ut_addPathToRoute(route: tp.IParamRouter, path: string): tp.IParamRouter {
  return {
    path: route.path + '/' + path,
    query: route.query,
    hash: route.hash,
  }
}

// Navigate to new route with or without history
export function ut_navigateNewRoute(path: string, query: tp.IParamQuery, hash: string, doPush: boolean, queryAddType?: tp.IQueryAddType): void {
  const route: tp.IParamRouter = ut_returnNewRoute(path, query, hash, queryAddType)
  navigateTo(route, { replace: !doPush })
}

// Return a new route for NuxtLink
export function ut_returnNewRoute(path: string, query: tp.IParamQuery, hash: string, queryAddType?: tp.IQueryAddType): tp.IParamRouter {
  const indexStore = useAppIndexStore()
  const router = useRouter()
  const newParam: tp.IParamRouter = fn_getNewRouterParams({ path, query, hash }, indexStore.getFullPath, indexStore.state.paramData, queryAddType)

  if (!queryAddType?.noHash && !newParam.hash) {
    newParam.hash = router.currentRoute.value.hash || ''
  }

  return {
    path: newParam.path,
    query: newParam.query,
    hash: newParam.hash,
  }
}

// Set local storage
export function ut_setLocalStorage(type: ElocalStorageType, content: string): void {
  localStorage.setItem(ut_getLocalStorageKey(type), content)
}

// Get local storage
export function ut_getLocalStorage(type: ElocalStorageType): string {
  return localStorage.getItem(ut_getLocalStorageKey(type)) || ''
}

// Get key for local storage
export function ut_getLocalStorageKey(type: ElocalStorageType): string {
  const appConfig = useAppConfig()
  let res: string
  // '_h' is wilo specific for historic could be done better
  if (appConfig.useShoppingCartCountry && type === ElocalStorageType.ls_shoppingcart) {
    const rcco: string = useAppIndexStore().state.paramData.rcco
    res = ut_modifyCountry(rcco) + '_' + type
  }
  else {
    res = type
  }
  return appConfig.customer + '_' + res
}

// Return detail object/node by path
export function ut_getObjectByPath(node: pf.TPfNode, path: string): pf.TPfNode | undefined {
  const pathAr: string[] = path.split('/')
  const { resNode, resPathAr } = fn_getNodesByPath(node, pathAr, false)
  if (resPathAr.length === 0) {
    return resNode
  }
  else {
    return undefined
  }
}

// Return prop by path
export function ut_getPropByPath(node: pf.TPfNode, path: string): pf.TPfNode | undefined {
  let res: pf.TPfNode | undefined = undefined
  const pathAr: string[] = path.split('/')
  const { resNode, resPathAr } = fn_getNodesByPath(node, pathAr, true)
  if (resPathAr.length === 1) {
    const props = resNode.props
    if (props) {
      const vindex: number = props.findIndex(n => (n.attrs?.PSM === resPathAr[0]) || (n.nodeName === resPathAr[0]))
      if (vindex > -1) {
        res = props[vindex]
      }
    }
  }
  return res
}

// Return formatted detail value by path, index and delimiter can be defined
export function ut_getValuesByPath(node: pf.TPfNode, path: string, takevalues: boolean = true, index: number = -1, delimiter: string = ', '): string {
  let res: string = ''
  const prop = ut_getPropByPath(node, path)
  if (prop) {
    if (takevalues) {
      if (prop.values) {
        const values = prop.values
        // single value
        if (index !== -1 && values[index]) {
          res = values[index].desc
        // multiple values
        }
        else {
          if (values.length > 0) {
            res = values.map(value => value.desc).join(delimiter)
          }
        }
      }
    }
    else {
      res = prop.desc
    }
  }
  return res
}

// Return formatted list value but search prop by name
export function ut_getListValuesByPropName(propName: string, props: pf.TPfNode[] | undefined, values: pf.TPfValue[] | undefined, index: number = -1, delimiter: string = ', '): string {
  let res: string = ''
  if (values && props) {
    res = ut_getListValues(props.find(i => i.nodeName === propName), values, index, delimiter)
  }
  return res
}

// Return formatted list value by prop, index and delimiter can be defined
export function ut_getListValues(prop: pf.TPfNode | undefined, values: pf.TPfValue[] | undefined, index: number = -1, delimiter: string = ', '): string {
  let res: string = ''
  if (values && prop) {
    const propValues: pf.TPfValue[] = ut_filterListValues(prop, values)
    if (propValues.length) {
      if (index === -1) {
        res = propValues.map(value => value.desc).join(delimiter)
      }
      else {
        res = propValues[index]?.desc || ''
      }
    }
  }
  return res
}

// Return value matching to prop by id
export function ut_filterListValues(prop: pf.TPfNode | undefined, values: pf.TPfValue[] | undefined): pf.TPfValue[] {
  if (values && prop) {
    return values.filter(value => value.propID === prop.nodeID)
  }
  else {
    return []
  }
}

// Return translation for key from translation value list
export function ut_getText(key: string, placeholderAr: string[] = [], paramData: tp.IParamData = useAppIndexStore().state.paramData): string {
  const initData: pf.InitData | undefined = useInitStore().getInitData(paramData)
  let res: string = '[' + key + ']'
  if (initData && initData.trans) {
    res = initData.trans[key] || '[' + key + ']'
    if (res) {
      for (const s of placeholderAr) {
        res = res.replace('%s', s)
      }
    }
  }
  return res // res.replace(/<[^>]*>/g, '') || '[' + key + ']'
}

// Return translation for key from footNotes value list
export function ut_getFootNote(key: string, paramData: tp.IParamData = useAppIndexStore().state.paramData): string {
  const initData: pf.InitData | undefined = useInitStore().getInitData(paramData)
  let res: string = '[' + key + ']'
  if (initData && initData.footNotes) {
    res = initData.footNotes[key] || '[' + key + ']'
  }
  return res
}

/*
 * Something a person typed, turned into a name a file system and a url can carry.
 *
 * `normalize('NFKD')` splits an accent off its letter and `\p{M}` drops it — both built in, so this
 * needs no language table: é becomes e, Č becomes C, İ becomes I, for every latin script at once.
 * ⚑ What cannot be decomposed is NOT transliterated — ß and Ł become a hyphen, and a name written in
 * cyrillic or japanese comes back EMPTY. That is on purpose: the alternative is a table that is
 * right for german and wrong for everyone else.
 *
 * Empty is therefore a normal answer, not a failure — the caller supplies its own fallback, because
 * only the caller knows one.
 */
export function ut_fileNameSafe(text: string, maxLength: number = 60): string {
  return (text || '')
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .replace(/[^a-zA-Z0-9-_]+/g, '-')
    .replace(/-{2,}/g, '-')
    .slice(0, maxLength)
    .replace(/^[-_]+|[-_]+$/g, '')
}

// Strip the footnote marker ({$footnote=…}) from a desc for plain display.
export function ut_stripFootNote(desc: string): string {
  return desc.split(def_footNote)[0] || desc
}

// Strip the footnote marker, collect its resolved text into `notes` (deduped) and mark the text
// with a superscript. Shared by list/table renderers that show a footnote legend below.
export function ut_footNoteMarked(val: string, notes: string[]): string {
  if (!val.includes(def_footNote)) { return val }
  const split: string[] = val.split(def_footNote)
  const note: string = '* ' + ut_getFootNote((split[1] || '').replace('}', ''))
  if (!notes.includes(note)) { notes.push(note) }
  return (split[0] || '') + ' <sup>*</sup>'
}

// Add new GET params to existing URL
export function ut_addUrlParams(urlstring: string, params: Record<string, any>): string {
  const url: URL = new URL(urlstring)
  const search_params = url.searchParams
  Object.entries(params).forEach(([param, value]): void => {
    // Skip only truly-unset values; keep 0 / false (legitimate filter/flag values that must reach pfinder).
    if (value !== undefined && value !== null && value !== '') {
      if (typeof value !== 'string') {
        value = JSON.stringify(value)
      }
      search_params.set(param, value)
    }
  })
  url.search = search_params.toString()
  return url.href
}

// Cap a reactive client-side cache map: drop the oldest entries (JS keys keep insertion order)
// until at most `max` remain. Prevents unbounded growth of the list/detail/mask/... stores over a
// long SPA session. Evicting an old entry is safe — a later visit just re-diffs to a cache miss and
// refetches.
export function ut_capStore(state: Record<string, unknown>, max: number = 50): void {
  const keys = Object.keys(state)
  const excess = keys.length - max
  for (let i = 0; i < excess; i++) {
    const key = keys[i]
    if (key !== undefined) {
      Reflect.deleteProperty(state, key) // triggers Vue reactivity via the proxy deleteProperty trap
    }
  }
}

// Format a file size depending on size
export function ut_formatFileSize(bytes: number, sizes: string[] = ['B', 'KB', 'MB', 'GB', 'TB'], separator: string = ' ', digits: number = 1): string {
  if (bytes) {
    const i = Math.min(parseInt(String(Math.floor(Math.log(bytes) / Math.log(1024))), 10), sizes.length - 1)
    return (bytes / (1024 ** i)).toFixed(digits) + separator + sizes[i]
  }
  return ''
}

// Format a number using country specific ISO639
export function ut_formatNumber(number: number, minDecimalsCount: number | undefined, maxDecimalsCount: number | undefined, decimalNoZero: boolean): string {
  const formatter = new Intl.NumberFormat(useAppIndexStore().state.paramData.iso639 || 'de-DE', {
    minimumFractionDigits: minDecimalsCount,
    maximumFractionDigits: maxDecimalsCount,
    trailingZeroDisplay: decimalNoZero ? 'stripIfInteger' : 'auto' },
  )
  return formatter.format(number)
}

// Format a number for schema data
export function ut_formatSchemaNumber(numberStr: string): string {
  const decSep: string = useAppIndexStore().state.paramData.decSep
  const thpSep: string = useAppIndexStore().state.paramData.thpSep
  numberStr = numberStr.replace(decSep + '-', '')
  numberStr = numberStr.replace(thpSep, '')
  numberStr = numberStr.replace(decSep, '\u002E')
  return numberStr
}

// Format a number for schema data
export function ut_getNumFromStr(numberStr: string): number {
  const decSep: string = useAppIndexStore().state.paramData.decSep
  const thpSep: string = useAppIndexStore().state.paramData.thpSep
  numberStr = numberStr.replace(thpSep, '')
  numberStr = numberStr.replace(decSep, '.')
  return Number(numberStr)
}

// Read special meaning attribut
export function ut_psmName(node: pf.TPfNode) {
  return (node.attrs && node.attrs['PSM']) ? node.attrs['PSM'] : node.nodeName
}

// Check screensize
export function ut_isScreenSize(screenSize: EScreenSize[]): boolean {
  return screenSize.includes(useAppIndexStore().state.screenSize)
}

// Is called for print
export function ut_isPrint(): boolean {
  return !!useAppIndexStore().state.paramData.print
}

// Embedded in e.g. CMS
export function ut_isEmbedded(): boolean {
  return !!useAppIndexStore().state.paramData.embeddedInfo.embedded
}

// Logged in
export function ut_isLoggedIn(): boolean {
  return useAppIndexStore().state.paramData.loggedin
}

// Modify customer specific country, could be perhaps done better
export function ut_modifyCountry(country: string): string {
  let res: string = country
  if (useAppConfig().customer === 'wilo') {
    if (country.slice(-2) === '_h') {
      res = country.slice(0, -2)
    }
  }
  return res
}

// get unit from value string
export function ut_getUnit(desc: string): string {
  return desc.split(String.fromCharCode(160))[1] || ''
}

// Expand a psx string ("combo|v1|v2,combo2|v3") into single "combo|value" selection items.
export function ut_getSelectedFromPsx(psx: string): string[] {
  const res: string[] = []
  if (!psx) { return res }
  for (const part of psx.split(',')) {
    const tAr: string[] = part.split('|')
    for (let d = 1; d < tAr.length; d++) {
      res.push(tAr[0] + '|' + tAr[d])
    }
  }
  return res
}

// Flatten a mask's filter nodes (top level + one nesting level).
export function ut_getFilterNodes(maskData: pf.MaskNode): pf.TPfNode[] {
  const res: pf.TPfNode[] = []
  for (const node of maskData.nodes) {
    if (node.nodeType === NodeType.nt_SearchFilter) {
      res.push(node)
    }
    else if (node.nodes) {
      res.push(...node.nodes)
    }
  }
  return res
}

// Description of one selected filter item ("combo|value"); withName prefixes the filter name
// ("Name: Value") for stored snapshots. withName=false = plain chip label.
export function ut_getFilterDescr(item: string, filterNodes: pf.TPfNode[], withName = false): string {
  const split: string[] = item.split('|')
  const node: pf.TPfNode | undefined = filterNodes.find(i => i.nodeName === split[0])
  if (!node || !node.values) { return '' }
  const nodeDesc: string = ut_stripFootNote(node.desc)
  // Slider: node.desc + range (value desc carries the unit).
  if (node.attrs?.SGO === '4' && node.values[0]) {
    const unit: string = ' ' + ut_getUnit(node.values[0].desc)
    const valAr: string[] = (split[1] || '').split('_')
    const range: string = (valAr[0] ? String.fromCharCode(8805) + valAr[0] + unit : '') + ' ' + (valAr[1] ? String.fromCharCode(8804) + valAr[1] + unit : '')
    return nodeDesc + (withName ? ': ' + range.trim() : ' | ' + range)
  }
  // Checkbox
  const value: pf.TPfValue | undefined = node.values.find(i => i.GUID === split[1])
  if (!value) { return '' }
  const valueDesc: string = ut_stripFootNote(value.desc)
  return (withName ? nodeDesc + ': ' : '') + valueDesc
}

// Get frequency from country
export function ut_getCountryValue(propName: string): pf.TPfValue[] {
  let res: pf.TPfValue[] = []
  const indexStore = useAppIndexStore()
  const initStore = useInitStore()
  const initData: pf.InitData | undefined = initStore.getInitData(indexStore.state.paramData)
  if (initData) {
    const country: pf.TPfNode | undefined = initData.restData.rcco.find(i => i.cod.toLowerCase() === indexStore.state.paramData.rcco.toLowerCase())
    if (country) {
      const prop: pf.TPfNode | undefined = ut_getPropByPath(country, propName)
      if (prop && prop.values) {
        res = prop.values
      }
    }
  }
  return res
}

// Log to server via the base pfinder `log` command (msg/level/ns are its own parameters).
// Appends to <docRoot>/logs/<ns>-<level>.log on the server. Deliberately a plain $fetch instead
// of the full pfinder API: logging is fire-and-forget and must not trigger loading state,
// error notifications or param processing — a failing log call only goes to the console.
export async function ut_sendToLog(
  postData: string,
  level: pf.PfCmd<pf.PfCmdType.LOG>['arg']['level'] = 'info',
  ns: string = 'general',
): Promise<void> {
  const runtimeConfig = useRuntimeConfig()
  const url: string = [runtimeConfig.public.dataHost || '', runtimeConfig.public.pfinderService || '', PfCmdType.LOG].join('/')
  try {
    await $fetch(url, { method: 'POST', body: { msg: postData, level, ns } })
  }
  catch (err) {
    const e = err as Error
    console.error(e)
  }
}
